import { EditPage, EditPageTextField, EditPageButton, EditPageSelect, EditPagePanel, EditPageTextArea, EditPageCheckbox } from './../../../common/EditPage';
import { create, getOne } from './../../../common/v4/BackendProvider';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SelectContainer from '../../../common/SelectContainer';

const AiToolsCreate = () => { 
    const [data, setData] = useState({status: true});
    const [metadata, setMetadata] = useState({});
    const [params, setParams] = useState([]);
    const navigate = useNavigate();
    useEffect(() => {
        getOne('/admin/ai/tools/', 'new').then(response => {
            setMetadata(response.data.metadata);
        });
    }, []);
    useEffect(() => {
        if (data.function_id) {
            getOne('/admin/ai/functions/', data.function_id).then(response => {
                const functionParams = response.data.data.params;
                
                // Update options for MessageTemplate parameters based on channel
                const updatedParams = functionParams.map(param => {
                    if (param.type && param.type.startsWith('model:MessageTemplate|channel=')) {
                        const channel = param.type.split('=')[1];
                        if (channel === 'email' && metadata.message_templates_email) {
                            param.options = metadata.message_templates_email;
                        } else if (channel === 'whatsapp' && metadata.message_templates_whatsapp) {
                            param.options = metadata.message_templates_whatsapp;
                        } else if (channel === 'sms' && metadata.message_templates_sms) {
                            param.options = metadata.message_templates_sms;
                        }
                    } else if (param.type && param.type.startsWith('model:MailAccount')) {
                        param.options = metadata.email_accounts;
                    }
                    return param;
                });
                
                setParams(updatedParams);
                
                // Update data.params with the parameters and initialize agent_provided flags
                setData(prevData => ({
                    ...prevData,
                    params: updatedParams.map(param => ({
                        ai_function_param_id: param.id,
                        value: param.value
                    })),
                    // Initialize agent_provided flags for each parameter
                    ...updatedParams.reduce((acc, param) => {
                        acc[`${param.id}_agent_provided`] = false;
                        acc[`${param.id}_description`] = '';
                        return acc;
                    }, {})
                }));
            }).catch(error => {
                toast.error("Error loading function parameters: " + error.message);
            });
        }
    }, [data.function_id, metadata]);

    const handleSave = () => {
        // Validate that parameter descriptions are provided only when AI Agent is checked
        const missingDescriptions = params.filter(param => {
            const isAgentProvided = data[`${param.id}_agent_provided`] || false;
            const hasDescription = data[`${param.id}_description`] && data[`${param.id}_description`].trim() !== '';
            return isAgentProvided && !hasDescription;
        });
        
        if (missingDescriptions.length > 0) {
            const paramNames = missingDescriptions.map(param => param.description || `Parameter ${param.id}`).join(', ');
            toast.error(`Please provide descriptions for parameters that will be filled by AI Agent: ${paramNames}`);
            return;
        }
        
        // Prepare the data to send, including agent_provided flags and descriptions
        const saveData = {
            ...data,
            agent_provided_flags: params.reduce((acc, param) => {
                acc[param.id] = data[`${param.id}_agent_provided`] || false;
                return acc;
            }, {}),
            param_descriptions: params.reduce((acc, param) => {
                acc[param.id] = data[`${param.id}_description`] || '';
                return acc;
            }, {})
        };
        
        create('/admin/ai/tools', {data: saveData}).then(response => {
            toast.success("AI Tool created successfully", {});
            navigate('/ai/tools');
        }).catch(error => {
            toast.error("Error creating AI Tool: " + error.message, {});
        });
    }
    const handleCancel = () => {
        navigate('/ai/tools');
    }
    const onChange = (id, value) => {
        setData({ ...data, [id]: value });
    }

    const onParamChange = (id, value) => {
        setParams(prevParams => prevParams.map(param => param.id === id ? { ...param, value } : param));
        setData(prevData => ({
            ...prevData,
            params: prevData.params?.map(param => 
                param.ai_function_param_id === id 
                    ? { ...param, value } 
                    : param
            ) || []
        }));
    }
    return (
        <div>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                closeOnClick={true}
                pauseOnHover={true}
                draggable={true}
                progress="undefined"
            />
            <EditPage handleSave={ handleSave } handleCancel={ handleCancel } title="Create AI Tool" >
                <EditPagePanel className="col-lg-6">
                    <EditPageTextField 
                        label="Name (Required, lowercase letters, numbers, and underscores)" 
                        id="name" 
                        onChange={ onChange } 
                        value={ data.name } 
                        pattern="[a-z0-9_]+"
                        placeholder="e.g., email_sender, sms_notifier, data_processor"
                    />
                    <EditPageTextField label="Description (Required, passed to AI Agent)" id="description" onChange={ onChange } value={ data.description } />
                    <EditPageSelect label="Function" id="function_id" onChange={ onChange } value={ data.function_id } options={ metadata?.functions } />
                    <EditPageCheckbox label="Is Active" id="status" onChange={ onChange } value={ data.status } />
                </EditPagePanel>
                <EditPagePanel className="col-lg-6">             
                    {params &&
                        <>
                            {params.map(param => (
                                <div key={param.id} style={{ marginBottom: '20px', padding: '15px', border: '1px solid #e9ecef', borderRadius: '5px' }}>
                                    {param.presentation_type === 'textarea' ?
                                        <EditPageTextArea 
                                            label={param.required ? param.description + " (Required)" : param.description}  
                                            id={param.id} 
                                            onChange={onParamChange} 
                                            value={param.value} 
                                            disabled={data[`${param.id}_agent_provided`] || false}
                                        /> :
                                        param.presentation_type === 'select' ?
                                        <SelectContainer 
                                            label={param.required ? param.description + " (Required)" : param.description}  
                                            id={param.id} 
                                            onChange={onParamChange} 
                                            value={param.value} 
                                            options={param.options} 
                                            disabled={data[`${param.id}_agent_provided`] || false}
                                        /> :
                                        <EditPageTextField 
                                            label={param.required ? param.description + " (Required)" : param.description}  
                                            id={param.id} 
                                            onChange={onParamChange} 
                                            value={param.value} 
                                            disabled={data[`${param.id}_agent_provided`] || false}
                                        />
                                    }
                                    <EditPageCheckbox 
                                        label="Fill by AI Agent" 
                                        id={`${param.id}_agent_provided`} 
                                        onChange={onChange} 
                                        value={data[`${param.id}_agent_provided`] || false} 
                                    />
                                    {(data[`${param.id}_agent_provided`] || false) && (
                                        <div style={{ 
                                            fontSize: '12px', 
                                            color: '#6c757d', 
                                            fontStyle: 'italic',
                                            marginTop: '5px',
                                            marginBottom: '10px'
                                        }}>
                                            This parameter will be filled automatically by the AI agent
                                        </div>
                                    )}
                                    <EditPageTextField 
                                        label="Parameter Description (Required when AI Agent is checked)" 
                                        id={`${param.id}_description`} 
                                        onChange={onChange} 
                                        value={data[`${param.id}_description`] || ''} 
                                        placeholder="Description for this parameter"
                                        required={data[`${param.id}_agent_provided`] || false}
                                        disabled={!(data[`${param.id}_agent_provided`] || false)}
                                    />
                                </div>
                            ))}
                        </>
                    || <div>No parameters</div>}
                    <div></div>
                </EditPagePanel>
            </EditPage>
        </div>
    )
}

export default AiToolsCreate;